import { ODSClose, ODSHelp, ODSLightDarkMode, ODSLink } from '@telekom-ods/react-icons';
import {
  ODSAvatar,
  ODSColumn,
  ODSSideNavigation,
  type ODSSideNavigationItemProps,
} from '@telekom-ods/react-ui-kit';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router';
import { useUserData } from 'src/redux/selectors';

import useAppContext from '../../hooks/useAppContext';
import useAuth from '../AppLayout/hooks/useAuth';
import useLayoutPermissions from '../AppLayout/hooks/useLayoutPermissions';
import useSideNavItems from '../AppLayout/hooks/useSideNavItems';

import BottomCollapsedItem from './BottomCollapsedItem';
import NavItem from './NavItem';
import SideNavigationHeader from './SideNavigationHeader';

interface SideNavigationMainProps {
  authenticated?: boolean;
}

const SideNavigationMain = ({ authenticated }: SideNavigationMainProps) => {
  const [expanded, setExpanded] = useState(true);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const intl = useIntl();
  const isAuthenticated = authenticated;
  const { toggleTheme, theme, selectedOrganization } = useAppContext();

  const userState = useUserData();
  const { organization } = userState;
  const { roles: authRoles } = useAuth();

  const currentOrganization = selectedOrganization ?? organization;
  const permissions = useLayoutPermissions(
    authRoles as Parameters<typeof useLayoutPermissions>[0],
    currentOrganization
  );
  const items = useSideNavItems({
    intl,
    permissions,
    navigate,
    pathname,
    selectedOrganization,
    ownOrganizationData: organization ?? null,
    isAuthenticated,
    isUserDataLoaded: Boolean(userState._id),
  });

  const typedItems = items as (ODSSideNavigationItemProps & {
    onClick?: () => void;
    items?: ({ onClick?: () => void } & Record<string, unknown>)[];
  })[];

  const hasOwnOrganizationItem = typedItems.some(
    item => (item as unknown as Record<string, unknown>)['data-own-organization-toggle'] === true
  );

  return (
    <ODSSideNavigation
      key={hasOwnOrganizationItem ? 'with-own-organization' : 'without-own-organization'}
      className='side-navigation-main'
      style={{ width: !expanded ? 'fit-content' : '' }}
      defaultExpanded
      navigationLeftIcon={<ODSClose />}
      expanded={expanded}
      showBottomBar
      navigationLeftIconButtonProps={{
        onClick: () => {
          setExpanded(previousExpanded => !previousExpanded);
        },
      }}
      headerSlot={<SideNavigationHeader theme={theme} />}
      bottomLeftSlot={
        expanded ? (
          <div
            style={{
              width: '100%',
              padding: '0 1rem 0 1rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              justifyItems: 'left',
              alignItems: 'flex-start',
            }}
          >
            <BottomCollapsedItem
              {...(authenticated && { authenticated, ownOrganizationData: organization ?? null })}
            />
          </div>
        ) : (
          <ODSColumn
            style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            {authenticated && (
              <NavItem
                {...{
                  to: `/docs/${import.meta.env.VITE_CONFLUENCE_HOMEPAGE}`,
                  theme,
                  label: intl.formatMessage({ id: 'docs' }),
                  icon: <ODSLink />,
                  collasped: true,
                }}
              />
            )}
            <NavItem
              {...{
                to: '/support-and-feedback',
                theme,
                label: intl.formatMessage({ id: 'help' }),
                icon: <ODSHelp />,
                collasped: true,
              }}
            />
            <ODSAvatar
              icon={<ODSLightDarkMode />}
              name='theme-toggle'
              showBackground
              size='large'
              variant='icon'
              onClick={toggleTheme}
            />
          </ODSColumn>
        )
      }
      items={typedItems}
      onItemClicked={(itemIndex, subItemIndex) => {
        const item = typedItems[itemIndex];
        if (typeof subItemIndex === 'number') {
          item?.items?.[subItemIndex]?.onClick?.();
          return;
        }
        item?.onClick?.();
      }}
      onExpandedChanged={expand => {
        setExpanded(expand);
      }}
    />
  );
};

export default SideNavigationMain;
